import React, { useState } from 'react';
import './NDIS.css';

function NDIS() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    ndisNumber: '',
    participant: '',
    supportAreas: [],
    message: '',
    agreeToTerms: false
  });

  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      supportAreas: checked
        ? [...prev.supportAreas, value]
        : prev.supportAreas.filter(item => item !== value)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Here you would typically send the data to your backend
    console.log('Form submitted:', formData);
    setSubmitted(true);
    // Reset form after submission
    setTimeout(() => {
      setFormData({
        name: '',
        email: '',
        phone: '',
        ndisNumber: '',
        participant: '',
        supportAreas: [],
        message: '',
        agreeToTerms: false
      });
      setSubmitted(false);
    }, 5000);
  };

  const supportAreas = [
    { id: 'clothing', label: 'Adaptive Clothing', icon: '👕' },
    { id: 'daily-living', label: 'Daily Living Aids', icon: '🛁' },
    { id: 'mobility', label: 'Mobility Equipment', icon: '♿' },
    { id: 'sensory', label: 'Sensory Products', icon: '🎧' },
    { id: 'therapy', label: 'Therapy Supports', icon: '💆' },
    { id: 'home-mod', label: 'Home Modifications', icon: '🏠' }
  ];

  const ndisSteps = [
    {
      number: '01',
      title: 'Check Eligibility',
      description: 'Verify your eligibility for NDIS funding through the NDIA portal or with your support coordinator.',
      icon: '✅'
    },
    {
      number: '02',
      title: 'Create Your Plan',
      description: 'Work with your planner to create an NDIS plan that includes clothing and adaptive equipment.',
      icon: '📋'
    },
    {
      number: '03',
      title: 'Choose The Shapes United',
      description: 'Select us as your preferred provider for adaptive clothing and sensory products.',
      icon: '👔'
    },
    {
      number: '04',
      title: 'Place Your Order',
      description: 'Use your NDIS funding to order products through our easy online system.',
      icon: '🛒'
    },
    {
      number: '05',
      title: 'Get Support',
      description: 'Our team will guide you through the entire process and handle all NDIS paperwork.',
      icon: '🤝'
    }
  ];

  const faqs = [
    {
      question: 'What NDIS categories can I use to purchase your products?',
      answer: 'Our products fall under Core Supports - Assistance with Daily Life, and Capacity Building - Improved Daily Living. Common budget lines include "Assistive Technology" and "Daily Activities".'
    },
    {
      question: 'Do I need approval from my support coordinator?',
      answer: 'Yes, for most items, you should discuss with your support coordinator or planner to ensure the products align with your plan goals and are included in your budget.'
    },
    {
      question: 'How do I pay with my NDIS funding?',
      answer: 'We accept Service Agreements, Plan Management provider payments, and NDIA-managed payments. Simply provide your NDIS details during checkout.'
    },
    {
      question: 'Can I try products before purchasing?',
      answer: 'Yes! We offer free samples and consultations. Contact our NDIS team to arrange a fitting session or sample kit.'
    },
    {
      question: 'What if I need custom modifications?',
      answer: 'We specialize in custom adaptive clothing. Our design team can work with you to create personalized solutions covered by your NDIS plan.'
    }
  ];

  return (
    <div className="ndis-page">
      {/* Hero Section */}
      <section className="ndis-hero">
        <div className="container">
          <div className="hero-grid">
            <div className="hero-content">
              <div className="ndis-badge">
                <span className="badge-icon">🇦🇺</span>
                <span className="badge-text">Registered NDIS Provider</span>
              </div>
              <h1>NDIS Clothing & Support Products</h1>
              <p className="hero-subtitle">
                Quality adaptive clothing and sensory products funded through your NDIS plan. 
                Making daily life more comfortable and independent.
              </p>
            </div>
            <div className="hero-image">
              <div className="image-frame">
                <div className="hero-image-content">
                  <div className="image-overlay">
                    <div className="image-text">
                      <h3>Adaptive Clothing Solutions</h3>
                      <p>Designed for comfort, dignity, and independence</p>
                    </div>
                  </div>
                  <div className="image-pattern">
                    {/* Geometric pattern placeholder */}
                    <div className="pattern-dot"></div>
                    <div className="pattern-dot"></div>
                    <div className="pattern-dot"></div>
                    <div className="pattern-line"></div>
                    <div className="pattern-circle"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-choose-section">
        <div className="container">
          <div className="section-intro">
            <div className="intro-badge">
              <span className="badge-dot"></span>
              WHY CHOOSE US
              <span className="badge-dot"></span>
            </div>
            <h2 className="section-title">The Shapes United Advantage</h2>
            <p className="section-description">
              As a registered NDIS provider, we combine expertise with empathy to deliver 
              exceptional adaptive clothing solutions that truly make a difference.
            </p>
          </div>

          <div className="advantages-grid">
            {/* Card 1 */}
            <div className="advantage-card card-1">
              <div className="card-number">01</div>
              <div className="card-icon">
                <div className="icon-wrapper">
                  <span>💼</span>
                </div>
              </div>
              <div className="card-content">
                <h3>NDIS Experts</h3>
                <p>Our team specializes in NDIS funding and understands exactly how to help you maximize your plan benefits for adaptive clothing.</p>
                <ul className="card-features">
                  <li>✓ Registered NDIS provider</li>
                  <li>✓ Streamlined claims process</li>
                  <li>✓ Plan management support</li>
                </ul>
              </div>
            </div>

            {/* Card 2 */}
            <div className="advantage-card card-2">
              <div className="card-number">02</div>
              <div className="card-icon">
                <div className="icon-wrapper">
                  <span>🎯</span>
                </div>
              </div>
              <div className="card-content">
                <h3>Personalized Solutions</h3>
                <p>Every individual is unique. We offer custom fittings and modifications to ensure perfect comfort and functionality.</p>
                <ul className="card-features">
                  <li>✓ Free consultation & fitting</li>
                  <li>✓ Custom modifications</li>
                  <li>✓ Individualized care plans</li>
                </ul>
              </div>
            </div>

            {/* Card 3 */}
            <div className="advantage-card card-3">
              <div className="card-number">03</div>
              <div className="card-icon">
                <div className="icon-wrapper">
                  <span>⭐</span>
                </div>
              </div>
              <div className="card-content">
                <h3>Premium Quality</h3>
                <p>We use only the highest quality materials designed for durability, ease of care, and maximum comfort.</p>
                <ul className="card-features">
                  <li>✓ Australian-made materials</li>
                  <li>✓ Easy-care fabrics</li>
                  <li>✓ 12-month quality guarantee</li>
                </ul>
              </div>
            </div>

            {/* Card 4 */}
            <div className="advantage-card card-4">
              <div className="card-number">04</div>
              <div className="card-icon">
                <div className="icon-wrapper">
                  <span>🤝</span>
                </div>
              </div>
              <div className="card-content">
                <h3>End-to-End Support</h3>
                <p>From initial consultation to after-sales support, we're with you every step of the way.</p>
                <ul className="card-features">
                  <li>✓ Dedicated support specialist</li>
                  <li>✓ Free shipping & returns</li>
                  <li>✓ Ongoing product support</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="stats-bar">
            <div className="stat-item">
              <div className="stat-number">100%</div>
              <div className="stat-label">NDIS Compliant</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">500+</div>
              <div className="stat-label">Participants Served</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">24/7</div>
              <div className="stat-label">Support Available</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">★ 4.9</div>
              <div className="stat-label">Average Rating</div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="process-section">
        <div className="container">
          <div className="section-header">
            <h2>How to Order with NDIS Funding</h2>
            <p>Simple steps to access our products through your NDIS plan</p>
          </div>
          <div className="process-steps">
            {ndisSteps.map((step, index) => (
              <div key={index} className="step-card">
                <div className="step-number">{step.number}</div>
                <div className="step-icon">{step.icon}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <div className="section-header">
            <h2>Frequently Asked Questions</h2>
            <p>Everything you need to know about using NDIS with The Shapes United</p>
          </div>
          <div className="faq-grid">
            {faqs.map((faq, index) => (
              <div key={index} className="faq-card">
                <h3>{faq.question}</h3>
                <p>{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NDIS Form Section */}
      <section id="ndis-form" className="form-section">
        <div className="container">
          <div className="form-container">
            <div className="form-header">
              <h2>NDIS Order & Inquiry Form</h2>
              <p>Fill out this form and our NDIS specialist will contact you within 24 hours</p>
            </div>

            {submitted ? (
              <div className="success-message">
                <div className="success-icon">✓</div>
                <h3>Thank You!</h3>
                <p>Your NDIS inquiry has been submitted. Our specialist will contact you within 24 hours.</p>
                <p className="success-note">
                  In the meantime, you can browse our <a href="/products">NDIS-approved products</a> 
                  or call us directly at <a href="tel:1300000000">1300 000 000</a>.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="ndis-form">
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Smith"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john@example.com"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">Phone Number *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="04XX XXX XXX"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="ndisNumber">NDIS Number</label>
                    <input
                      type="text"
                      id="ndisNumber"
                      name="ndisNumber"
                      value={formData.ndisNumber}
                      onChange={handleChange}
                      placeholder="XXX-XXX-XXX-XXXX"
                    />
                  </div>

                  <div className="form-group full-width">
                    <label>Who are you inquiring for? *</label>
                    <div className="radio-group">
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="participant"
                          value="self"
                          checked={formData.participant === 'self'}
                          onChange={handleChange}
                          required
                        />
                        <span className="radio-custom"></span>
                        Myself
                      </label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="participant"
                          value="family"
                          checked={formData.participant === 'family'}
                          onChange={handleChange}
                        />
                        <span className="radio-custom"></span>
                        Family Member
                      </label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="participant"
                          value="client"
                          checked={formData.participant === 'client'}
                          onChange={handleChange}
                        />
                        <span className="radio-custom"></span>
                        Client (Support Worker)
                      </label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="participant"
                          value="planner"
                          checked={formData.participant === 'planner'}
                          onChange={handleChange}
                        />
                        <span className="radio-custom"></span>
                        Planner/Coordinator
                      </label>
                    </div>
                  </div>

                  <div className="form-group full-width">
                    <label>Areas of Support Needed</label>
                    <div className="checkbox-grid">
                      {supportAreas.map((area) => (
                        <label key={area.id} className="checkbox-label">
                          <input
                            type="checkbox"
                            value={area.id}
                            checked={formData.supportAreas.includes(area.id)}
                            onChange={handleCheckboxChange}
                          />
                          <span className="checkbox-custom"></span>
                          <span className="checkbox-icon">{area.icon}</span>
                          <span className="checkbox-text">{area.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="form-group full-width">
                    <label htmlFor="message">Additional Information</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about specific needs, preferences, or questions..."
                      rows="4"
                    ></textarea>
                  </div>

                  <div className="form-group full-width">
                    <label className="terms-label">
                      <input
                        type="checkbox"
                        name="agreeToTerms"
                        checked={formData.agreeToTerms}
                        onChange={handleChange}
                        required
                      />
                      <span className="checkmark"></span>
                      I agree to the <a href="/privacy">Privacy Policy</a> and understand that 
                      The Shapes United may contact me regarding my NDIS inquiry.
                    </label>
                  </div>

                  <div className="form-group full-width">
                    <button type="submit" className="submit-btn">
                      Submit NDIS Inquiry
                    </button>
                    <p className="form-note">
                      By submitting this form, you agree to our terms. Our NDIS specialist 
                      will contact you within 24 hours to discuss your needs.
                    </p>
                  </div>
                </div>
              </form>
            )}

            <div className="form-support">
              <h3>Need Immediate Assistance?</h3>
              <div className="support-options">
                <a href="tel:1300000000" className="support-option">
                  <span className="option-icon">📞</span>
                  <div>
                    <strong>Call NDIS Support</strong>
                    <span>1300 000 000</span>
                  </div>
                </a>
                <a href="mailto:ndis@theshapesunited.com" className="support-option">
                  <span className="option-icon">✉️</span>
                  <div>
                    <strong>Email Us</strong>
                    <span>ndis@theshapesunited.com</span>
                  </div>
                </a>
                <a href="/contact" className="support-option">
                  <span className="option-icon">💬</span>
                  <div>
                    <strong>Live Chat</strong>
                    <span>Available 9am-5pm AEST</span>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Resources Section */}
      <section className="resources-section">
        <div className="container">
          <div className="section-header">
            <h2>NDIS Resources & Guides</h2>
            <p>Helpful information to navigate your NDIS journey</p>
          </div>
          <div className="resources-grid">
            <a href="/ndis-guide" className="resource-card">
              <div className="resource-icon">📘</div>
              <h3>NDIS Buying Guide</h3>
              <p>Complete guide to purchasing adaptive clothing with NDIS funding</p>
              <span className="resource-link">Download Guide →</span>
            </a>
            <a href="/products" className="resource-card">
              <div className="resource-icon">👕</div>
              <h3>NDIS Product Catalog</h3>
              <p>Browse our range of NDIS-approved adaptive clothing and products</p>
              <span className="resource-link">View Products →</span>
            </a>
            <a href="/faqs" className="resource-card">
              <div className="resource-icon">❓</div>
              <h3>NDIS FAQs</h3>
              <p>Common questions about using NDIS for clothing and support items</p>
              <span className="resource-link">Read FAQs →</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

export default NDIS;
